import {
  createContext,
  useCallback,
  useContext,
  useInsertionEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ExploreSplash, type ExploreSplashProps } from './explore/ExploreSplash';

type Phase = ExploreSplashProps['phase'];
interface SceneState {
  phase: Phase;
  ready: boolean;
  error?: string;
}
const StartupContext = createContext<((state: SceneState) => void) | null>(null);

/** Reports presentation readiness only, never location eligibility or durable game outcomes. */
export function useSceneStartup(phase: Phase, ready: boolean, error?: string, enabled = true) {
  const report = useContext(StartupContext);
  useLayoutEffect(() => {
    if (enabled) report?.({ phase, ready, error });
  }, [report, phase, ready, error, enabled]);
}

export function SceneStartupBoundary({
  children,
  routeKey,
  phase,
  waitingForData,
  enabled,
  onLeave,
  onAlternative,
}: {
  children: ReactNode;
  routeKey: string;
  phase?: Phase;
  waitingForData: boolean;
  enabled: boolean;
  onLeave: () => void;
  onAlternative?: () => void;
}) {
  const [scene, setScene] = useState<SceneState & { key: string; complete: boolean }>();
  const [observedRoute, setObservedRoute] = useState(routeKey);
  if (observedRoute !== routeKey) {
    // History Back can reuse a key but remounts its renderer. Reset before children commit,
    // including when the intervening route has no readiness reporter.
    setObservedRoute(routeKey);
    setScene(undefined);
  }
  const activeRoute = useRef(routeKey);
  // Reject callbacks retained by a scene from an earlier committed navigation.
  useInsertionEffect(() => {
    activeRoute.current = routeKey;
  }, [routeKey]);
  const report = useCallback(
    (next: SceneState) => {
      setScene((previous) => {
        if (activeRoute.current !== routeKey) return previous;
        // A revealed route never re-splashes for GPS pings, query refreshes or renderer updates.
        if (previous?.key === routeKey && previous.complete) return previous;
        return { ...next, key: routeKey, complete: false };
      });
    },
    [routeKey],
  );
  const current = scene?.key === routeKey ? scene : undefined;
  const active = enabled && !current?.complete && (waitingForData || Boolean(phase));
  useLayoutEffect(() => {
    if (!current?.ready || current.complete || waitingForData) return;
    const pendingImages = [...document.querySelectorAll<HTMLImageElement>('#root img')].filter(
      (image) => {
        const bounds = image.getBoundingClientRect();
        return (
          bounds.width > 0 && bounds.height > 0 && (!image.complete || image.naturalWidth === 0)
        );
      },
    );
    const finish = (error?: string) =>
      setScene((previous) =>
        previous?.key === routeKey && previous.ready && !previous.complete
          ? { ...previous, ready: !error, complete: !error, error }
          : previous,
      );
    if (!pendingImages.length && document.fonts?.status !== 'loading') {
      finish();
      return;
    }
    let cancelled = false;
    let frame: number | undefined;
    void Promise.all([...pendingImages.map((image) => image.decode()), document.fonts?.ready])
      .then(() => {
        if (!cancelled)
          frame = window.requestAnimationFrame(() => {
            if (!cancelled) finish();
          });
      })
      .catch(() => {
        if (!cancelled)
          finish('Some of your game artwork could not load. Try again to open your world.');
      });
    return () => {
      cancelled = true;
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
  }, [current, routeKey, waitingForData]);
  useLayoutEffect(() => {
    // The modal child opens before this handoff; there is no blank frame between HTML and React.
    document.getElementById('azr-boot-splash')?.remove();
  }, []);

  return (
    <StartupContext.Provider value={report}>
      {children}
      <ExploreSplash
        active={active}
        holdUntilReady
        phase={waitingForData ? 'realm' : (current?.phase ?? phase ?? 'realm')}
        error={current?.error}
        onRetry={() => window.location.reload()}
        onLeave={onLeave}
        onAlternative={onAlternative}
      />
    </StartupContext.Provider>
  );
}
